namespace Nbs.Xlr8PageTemplate.Menu.MenuHtmlBuilder {
  export function getTopMenuElement(menuItems?: Navigation.MenuItem[]) {
    if (!menuItems || menuItems.length === 0) {
      return undefined;
    }

    const menuElements = createMenuItemElements(menuItems, 0, 0).filter(x => !!x);

    if (menuElements.length === 0) {
      return undefined;
    }

    return Utilities.createElement({
      tag: 'nav',
      attributes: {
        id: 'xptNavMenu',
      },
      classes: ['xpt-mobile-collapsed', 'xpt-scrollable'],
      children: [Utilities.createElement({ tag: 'ul', attributes: { role: 'menu' }, children: menuElements })]
    });
  }

  function createMenuItemElements(menuItems: Navigation.MenuItem[], level: number, expanderLevel: number) {
    return menuItems.map(menuItem => {
      return createMenuItemElement(menuItem, level, expanderLevel);
    });
  }

  function createMenuItemElement(menuItem: Navigation.MenuItem, level: number, expanderLevel: number) {
    const menuLink = (menuItem as Navigation.Link);
    const menu = (menuItem as Navigation.Menu);

    if (menu.childItems) {
      if (menu.displayMode === Navigation.DisplayMode.Expand) {
        return createExpanderMenuElement(menu, level, expanderLevel);
      }
      if (menu.displayMode === Navigation.DisplayMode.Flyout) {
        return createFlyoutMenuElement(menu, expanderLevel);
      }
      return createGroupMenuElement(menu, level);
    }
    return createLinkItemElement(menuLink, level, expanderLevel);
  }

  function createExpanderMenuElement(menu: Navigation.Menu, level: number, expanderLevel: number): HTMLElement | undefined {
    if (!menu.childItems || menu.childItems.length === 0) {
      return undefined;
    }
    if (!menu.label) {
      console.error(`expander sub menus must have a label`, menu.id, menu.label);
      return undefined;
    }
    if (level > 1) {
      console.error(`expander sub menus are only allowed to nest up to 2 levels, level=${level}, skipping menu "${menu.label}"`);
      return undefined;
    }

    if (expanderLevel !== 0 && menu.icon) {
      console.warn(`custom icon on expander sub menus are only supported at the top level, ignoring icon "${menu.icon}" at expander level=${expanderLevel} on "${menu.label}"`);
    }

    const id = Utilities.getNewId('xpt-expander-menu');

    return Utilities.createElement({
      tag: 'li',
      classes: ['xpt-expander-menu-item', 'xpt-collapsed'],
      attributes: { id, role: 'menuitem' },
      children: [
        Utilities.createElement({
          tag: 'a',
          attributes: {
            'aria-expanded': false,
            'aria-haspopup': true,
            'data-menu-item-id': menu.id,
            href: '#',
            role: 'button',
          },
          eventHandlers: { click: onExpanderClick },
          children: [
            Utilities.createElement({
              classes: ['xpt-menu-link-icon'],
              children: [Utilities.getIconElement(expanderLevel === 0 ? menu.icon : 'arrow_drop_down')]
            }),
            Utilities.createLinkLabelElement(menu),
            expanderLevel === 0 ? Utilities.createElement({
              classes: ['xpt-menu-link-expander-icon'],
              children: [Utilities.getIconElement('expand_more')]
            }) : undefined
          ]
        }),
        Utilities.createElement({
          tag: 'ul',
          classes: ['xpt-collapsed'],
          attributes: { role: 'menu' },
          children: createMenuItemElements(menu.childItems, level + 1, expanderLevel + 1)
        })
      ]
    });
  }

  function createFlyoutMenuElement(menu: Navigation.Menu, expanderLevel: number) {
    if (expanderLevel > 0) {
      console.warn(`context menus are not allowed within expander menus on "${menu.label}"`);
      return undefined;
    }

    return Utilities.createElement({
      tag: 'li',
      classes: ['xpt-menu-link-item'],
      attributes: { 'data-menu-item-id': menu.id, role: 'menuitem' },
      children: [Utilities.createElement({
        tag: 'a',
        attributes: {
          'aria-expanded': false,
          href: '#',
          role: 'button',
        },
        eventHandlers: { click: event => onFlyoutClick(event, menu.id) },
        children: [Utilities.createElement({
          classes: ['xpt-flyout-menu-item-container'],
          children: [
            Utilities.createElement({ children: [Utilities.getIconElement(menu.icon)] }),
            Utilities.createLinkLabelElement(menu),
            Utilities.createElement({ classes: ['xpt-flyout-arrow'], children: [Utilities.getIconElement('play_arrow')] })
          ]
        })]
      })]
    });
  }

  function createGroupMenuElement(menu: Navigation.Menu, level: number): HTMLElement | undefined {
    if (!menu.childItems || menu.childItems.length === 0) {
      return undefined;
    }
    if (level > 0) {
      console.warn(`grouped sub menus are only allowed at the top level, level=${level} on "${menu.label}"`);
      return undefined;
    }

    let menuLabelElement: HTMLElement | undefined;
    let labelledBy: string | undefined;
    if (menu.label) {
      const groupId = 'xpt-group-' + Utilities.getNextId();
      labelledBy = groupId;
      menuLabelElement = Utilities.createElement({ attributes: { id: groupId }, children: [menu.label] });
    }

    return Utilities.createElement({
      tag: 'li',
      classes: ['xpt-sub-menu-item'],
      attributes: { role: 'menuitem' },
      children: [
        menuLabelElement,
        Utilities.createElement({
          tag: 'ul',
          attributes: { 'aria-labelledby': labelledBy, role: 'menu' },
          children: createMenuItemElements(menu.childItems, level + 1, 0)
        })
      ]
    });
  }

  function createLinkItemElement(link: Navigation.Link, level: number, expanderLevel: number) {
    const id = !link.isDisabled ? link.id : undefined;
    
    const isEnabledToAddOpenInTabIcon = link.openInTab && expanderLevel == 0;

    if (expanderLevel > 0 && !!link.icon) {
      console.warn(`icons are only supported on menu items, ignoring icon "${link.icon}" at level=${level} on "${link.label}"`);
    }

    return Utilities.createElement({
      tag: 'li',
      classes: [ isEnabledToAddOpenInTabIcon ? 'xpt-expander-menu-item' : 'xpt-menu-link-item', link.isDisabled ? 'xpt-disabled' : ''],
      attributes: { 'aria-disabled': link.isDisabled ? true : undefined, 'data-menu-item-id': id, role: 'menuitem' },
      children: [Utilities.createElement({
        tag: 'a',
        attributes: Navigation.getLinkOnlyAttributes(link),
        eventHandlers: { click: onClick },
        children: [
          Utilities.createElement({
            classes: ['xpt-menu-link-icon'],
            children: [
              expanderLevel === 0 ? (link.icon ? Utilities.getIconElement(link.icon) : undefined) :
                Utilities.createElement({ tag: 'span', children: ['\u00A0'] })
            ]
          }),
          Utilities.createLinkLabelElement(link),
          isEnabledToAddOpenInTabIcon ? Utilities.createElement({ classes: ['open-in-new-tab-icon'], children: [Utilities.getIconElement('open_in_new')] }) : undefined
        ]
      })]
    });
  }
}
